import styled from 'styled-components';
import { NavLink } from 'react-router-dom';

import { Text } from '../../../../components/Text/Text';
import { ANIM, LAYOUT_CHROME, SIDE_MENU_MORE } from '../../../../theme/tokens';

/**
 * The flyout surface. Rendered in a portal to <body> and positioned `fixed`
 * (left/bottom/max-height set inline from the trigger's viewport rect), so it
 * floats above the content card regardless of any page-transition transform /
 * stacking context, and clamps to the viewport (the inner scroller takes over
 * when it can't fit). Visual geometry from Figma node 8927:821 (20px radius, the
 * `#18181b` surface, big soft shadow); width hugs the longest label.
 */
export const Surface = styled.div<{ $open: boolean }>`
  position: fixed;
  z-index: 1000;

  width: max-content;
  min-width: 174px;
  max-width: 280px;

  display: flex;
  flex-direction: column;
  overflow: hidden; /* clip the fade overlays + the rounded corners */

  border-radius: 20px;
  background: ${SIDE_MENU_MORE.bg};
  border: 1px solid ${SIDE_MENU_MORE.border};
  box-shadow: ${SIDE_MENU_MORE.shadow};

  transform-origin: bottom left;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? 'none' : 'translateX(-6px) scale(0.96)')};
  transition:
    opacity ${({ $open }) => ($open ? ANIM.MENU_OPEN_MS : ANIM.MENU_CLOSE_MS)}ms ${ANIM.EASE},
    transform ${({ $open }) => ($open ? ANIM.MENU_OPEN_MS : ANIM.MENU_CLOSE_MS)}ms ${ANIM.EASE};
  will-change: opacity, transform;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
    transform: none;
  }
`;

/* The scroll viewport — vertical only (overflow-x explicitly hidden so the
   `overflow-y: auto` quirk can't promote overflow-x to `auto` and add a stray
   horizontal scrollbar). Native scrollbar hidden; the fade overlays signal more. */
export const Scroll = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 8px 16px 16px;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

/* Figma `menu items` — a column of rows, 16px apart. Width hugs the content. */
export const Items = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/* Top / bottom fade overlays — only shown when there's scrollable content in
   that direction; rows dissolve into the panel surface so the list never looks
   hard-cut. */
export const Fade = styled.div<{ $edge: 'top' | 'bottom'; $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  height: 28px;
  z-index: 2;
  pointer-events: none;
  ${({ $edge }) => ($edge === 'top' ? 'top: 0;' : 'bottom: 0;')}
  background: ${({ $edge }) => ($edge === 'top' ? SIDE_MENU_MORE.fadeTop : SIDE_MENU_MORE.fadeBottom)};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

/* One row = a 40px icon slot (the rail's own 24px icon, centred, no chip) + the
   label. Rest colours from Figma; hover/active recolour to the nav accent, the
   same interaction the rail uses. */
export const Row = styled(NavLink)<{ $index: number; $open: boolean }>`
  display: flex;
  align-items: center;
  height: 48px;
  border-radius: 12px;

  cursor: pointer;
  user-select: none;
  text-decoration: none;
  color: ${SIDE_MENU_MORE.label};

  /* Per-row entrance: fade + small rise, staggered on open only. */
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? 'none' : 'translateY(4px)')};
  transition:
    opacity ${ANIM.MENU_OPEN_MS}ms ${ANIM.EASE},
    transform ${ANIM.MENU_OPEN_MS}ms ${ANIM.EASE},
    background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  transition-delay: ${({ $open, $index }) => ($open ? $index * ANIM.MENU_ROW_STAGGER_MS : 0)}ms;

  &:hover,
  &.active {
    background: ${SIDE_MENU_MORE.rowHover};
    color: ${LAYOUT_CHROME.navActive};

    .txt {
      color: ${LAYOUT_CHROME.navActive};
    }
    path[fill],
    circle[fill] {
      fill: ${LAYOUT_CHROME.navActive};
    }
    path[stroke] {
      stroke: ${LAYOUT_CHROME.navActive};
    }
    /* Mask-based icons recolour via background-color. */
    .icon-mask {
      background-color: ${LAYOUT_CHROME.navActive};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE}, color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }
`;

/* The 24px rail icon, centred in a 40px slot so the label lands flush at x=40
   exactly like Figma — but without the placeholder grey chip behind it. */
export const RowIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;

  svg {
    display: block;
    width: 24px;
    height: 24px;
  }

  .icon-mask {
    width: 24px;
    height: 24px;
    background-color: ${LAYOUT_CHROME.navIconIdle};
  }

  .m-bg {
    fill: white;
    fill-opacity: 0.32;
  }
`;

/* Figma label — Satoshi Bold 14/1.43, uppercased. Keeps the area font family
   (config-driven); takes only size/weight/case from Figma. `nowrap` so a long
   label widens the panel instead of wrapping or forcing a horizontal scroll. */
export const RowLabel = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.43;
  text-transform: uppercase;
  /* colour comes from the parent row / its active state */
`;
